import React, { useEffect } from "react";

export function Badge({ className = "", children, ...props }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[11px] font-mono font-semibold uppercase tracking-wider ${className}`}
      {...props}
    >
      {children}
    </span>
  );
}

export function Btn({ variant = "primary", size = "md", className = "", children, ...props }) {
  const variants = {
    primary: "bg-primary text-primary-foreground hover:brightness-110 border border-primary",
    outline: "bg-transparent border border-border text-foreground hover:bg-accent",
    ghost: "bg-transparent text-foreground hover:bg-accent border border-transparent",
    danger: "bg-destructive text-destructive-foreground hover:brightness-110 border border-destructive",
    success: "bg-emerald-600 text-white hover:bg-emerald-500 border border-emerald-600",
  };
  const sizes = { sm: "h-8 px-2.5 text-xs", md: "h-9 px-4 text-sm", lg: "h-11 px-6 text-base" };
  return (
    <button
      className={`inline-flex items-center justify-center gap-1.5 rounded-md font-semibold transition-all duration-150 active:scale-[0.97] disabled:opacity-50 disabled:pointer-events-none ${variants[variant]} ${sizes[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function Input({ className = "", ...props }) {
  return (
    <input
      className={`h-9 w-full rounded-md border border-input bg-background px-3 text-sm outline-none transition focus:border-primary focus:ring-1 focus:ring-primary ${className}`}
      {...props}
    />
  );
}

export function Textarea({ className = "", ...props }) {
  return (
    <textarea
      className={`w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none transition focus:border-primary focus:ring-1 focus:ring-primary ${className}`}
      {...props}
    />
  );
}

export function Select({ className = "", children, ...props }) {
  return (
    <select
      className={`h-9 w-full rounded-md border border-input bg-background px-2 text-sm outline-none transition focus:border-primary focus:ring-1 focus:ring-primary ${className}`}
      {...props}
    >
      {children}
    </select>
  );
}

export function Field({ label, children, required }) {
  return (
    <label className="block space-y-1">
      <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">
        {label} {required && <span className="text-red-500">*</span>}
      </span>
      {children}
    </label>
  );
}

export function Card({ className = "", children, ...props }) {
  return (
    <div className={`rounded-lg border border-border bg-card ${className}`} {...props}>
      {children}
    </div>
  );
}

export function Modal({ open, onClose, title, children, wide }) {
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center overflow-y-auto bg-black/60 backdrop-blur-sm sm:items-start sm:p-4" onMouseDown={onClose}>
      <div
        className={`animate-in-up w-full sm:mt-10 ${wide ? "sm:max-w-3xl" : "sm:max-w-xl"} max-h-[100dvh] overflow-y-auto rounded-t-2xl border border-border bg-card shadow-2xl sm:max-h-none sm:overflow-visible sm:rounded-lg`}
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card px-4 py-3 sm:static sm:px-5">
          <h3 className="font-heading text-lg font-bold uppercase tracking-wide">{title}</h3>
          <button onClick={onClose} className="-mr-2 flex h-10 w-10 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground" data-testid="modal-close-btn">✕</button>
        </div>
        <div className="p-4 sm:p-5">{children}</div>
      </div>
    </div>
  );
}
